/*
* AudioDatePicker.js (Please do not remove this comment)
* This file is part of the Audio Management System.
* This component is a date picker for selecting a date.
* A calendar component that displays selectable dates based on available audio files. 
* Allows users to pick a date with available audio recordings.
* The component uses the Material-UI library for styling and functionality.
* There are three properties (arguments) to be passed to this component:
* 1. availableDates: string[] – An array of dates in ISO format (YYYY-MM-DD) that have available audio files.
* 2. selectedDate: Date – The currently selected date.
* 3. onDateChange: (newDate: Date) => void – Callback function triggered when the user selects a new date.
*
* User Interactions:
*
* Displays a calendar with disabled dates when no audio files are available.
* Highlight dates with available audio files.
* When a user clicks a date with audio files, onDateChange is called with the selected date.
*
* my version:
* @mui/material: 7.1.0
* @mui/x-date-pickers: 8.3.0
* date-fns: 4.1.0
* wavesurfer.js: 7.9.5
*/

import React, { useEffect, useState } from 'react';
import { Paper, Typography, Box, Tooltip } from '@mui/material';
import { LocalizationProvider, DateCalendar, DayCalendarSkeleton } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid, isEqual, parseISO } from 'date-fns';

const AudioDatePicker = ({ availableDates = [], selectedDate, onDateChange }) => {
  const [availableDatesMap, setAvailableDatesMap] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // 將 availableDates 轉成 map 方便查詢
  useEffect(() => {
    const map = {};
    availableDates.forEach(dateStr => {
      try {
        // 支援 YYYY-MM-DD 或 ISO 字串
        const d = dateStr.length === 10 ? parseISO(dateStr + 'T00:00:00') : parseISO(dateStr);
        map[format(d, 'yyyy-MM-dd')] = true;
      } catch (e) {
        console.error('Invalid date format:', dateStr, e);
      }
    });
    setAvailableDatesMap(map);
    setIsLoading(false);
  }, [availableDates]);

  // 日期是否可選
  const shouldDisableDate = (day) => {
    const key = format(day, 'yyyy-MM-dd');
    return !availableDatesMap[key];
  };

  // 日期選擇
  const handleDateChange = (newDate) => {
    if (!newDate || !isValid(newDate)) return;
    const key = format(newDate, 'yyyy-MM-dd');
    if (availableDatesMap[key]) {
      onDateChange(newDate);
    }
  };

  // 自訂日期渲染 - 修改為適應 MUI v8.3.0 API
  const Day = (props) => {
    const { day, outsideCurrentMonth, ...other } = props;
    
    // 確保 day 是有效日期
    if (!day || !isValid(day) || outsideCurrentMonth) {
      return <Box {...other} />;
    }
    
    const key = format(day, 'yyyy-MM-dd');
    const hasAudio = availableDatesMap[key];
    const isSelected = selectedDate && isEqual(day, selectedDate);
    
    // 獲取日期數字
    const dayNumber = format(day, 'd');
    
    // 自定義點擊處理函數
    const handleDayClick = (event) => {
      // 如果日期有音訊可用，處理日期選擇
      if (hasAudio) {
        handleDateChange(day); // 直接調用你自己的日期處理函數
        
        // 仍然保留原始的 onClick，保持與 MUI 的相容性
        if (other.onClick) {
          other.onClick(event);
        }
      }
    };
    
    return (
      <Tooltip title={hasAudio ? 'Audio available' : 'No audio'}>
        <Box
          {...other}
          onClick={handleDayClick} // 添加自定義點擊處理
          sx={{
            ...other.sx,
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: 36,
            height: 36,
            margin: '0 2px',
            borderRadius: '50%',
            backgroundColor: isSelected ? 'primary.light' : hasAudio ? 'action.hover' : 'inherit',
            '&::after': hasAudio ? {
              content: '""',
              position: 'absolute',
              bottom: 2,
              right: 2,
              width: 5,
              height: 5,
              bgcolor: 'primary.main',
              borderRadius: '50%'
            } : {},
            cursor: hasAudio ? 'pointer' : 'default',
            color: hasAudio ? 'text.primary' : 'text.disabled',
          }}
        >
          {dayNumber}
        </Box>
      </Tooltip>
    );
  };

  return (
    <Paper elevation={3} sx={{ p: 2, maxWidth: '100%' }}>
      {/* <Typography variant="h6" gutterBottom align="center">
        Audio Date Picker
      </Typography> */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {isLoading ? (
          <DayCalendarSkeleton />
        ) : (
          <DateCalendar
            value={selectedDate}
            onChange={handleDateChange}
            shouldDisableDate={shouldDisableDate}
            slots={{
              day: Day
            }}
          />
        )}
      </LocalizationProvider>
      {/* <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {Object.keys(availableDatesMap).length > 0
            ? `${Object.keys(availableDatesMap).length} day(s) with available audio`
            : "No audio available currently"}
        </Typography>
        {selectedDate && isValid(selectedDate) && (
          <Typography variant="body1" fontWeight="medium" mt={1}>
            Selected: {format(selectedDate, 'yyyy-MM-dd')}
          </Typography>
        )}
      </Box> */}
    </Paper>
  );
};

export default AudioDatePicker;
